const SESSION_STORAGE_KEY = "mux.user.session"
const OAUTH_VERIFIER_KEY = "mux.oauth.verifier"

function randomBase64URL(bytes: number) {
  const value = new Uint8Array(bytes)
  crypto.getRandomValues(value)
  let binary = ""
  for (const byte of value) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

async function sha256Base64URL(value: string) {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value))
  let binary = ""
  for (const byte of new Uint8Array(digest)) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

export async function createOAuthVerifierChallenge() {
  const verifier = randomBase64URL(32)
  sessionStorage.setItem(OAUTH_VERIFIER_KEY, verifier)
  return sha256Base64URL(verifier)
}

export function readUserSessionToken() {
  try {
    return localStorage.getItem(SESSION_STORAGE_KEY) || ""
  } catch {
    return ""
  }
}

export function writeUserSessionToken(token: string) {
  try {
    if (!token) {
      localStorage.removeItem(SESSION_STORAGE_KEY)
    } else {
      localStorage.setItem(SESSION_STORAGE_KEY, token)
    }
  } catch {
  }
}

export function subscribeUserSession(changed: () => void) {
  const listener = (event: StorageEvent) => {
    if (event.storageArea === localStorage && (event.key === SESSION_STORAGE_KEY || event.key === null)) changed()
  }
  window.addEventListener("storage", listener)
  return () => window.removeEventListener("storage", listener)
}

export function userSessionHeaders(): Record<string, string> {
  const token = readUserSessionToken()
  if (!token) return {}
  return { "X-Mux-User-Session": token }
}

export function parseUserSessionHandoff(hash: string) {
  const params = new URLSearchParams(hash.replace(/^#/, ""))
  const code = params.get("mux_handoff") || ""
  if (!code.startsWith("muxhandoff1.")) return { code: "", hash }
  params.delete("mux_handoff")
  const remaining = params.toString()
  return { code, hash: remaining ? `#${remaining}` : "" }
}

export async function consumeUserSessionHandoff(exchange: (code: string, verifier: string) => Promise<string>) {
  if (typeof location === "undefined" || typeof history === "undefined") return false
  const handoff = parseUserSessionHandoff(location.hash)
  if (!handoff.code) return false
  history.replaceState(history.state, "", `${location.pathname}${location.search}${handoff.hash}`)

  let verifier = ""
  try {
    verifier = sessionStorage.getItem(OAUTH_VERIFIER_KEY) || ""
    sessionStorage.removeItem(OAUTH_VERIFIER_KEY)
  } catch {
    return false
  }
  if (!verifier) return false

  try {
    const token = await exchange(handoff.code, verifier)
    if (!token.startsWith("muxsess1.")) return false
    writeUserSessionToken(token)
    return true
  } catch {
    return false
  }
}
